import { TopNavigation, TopNavigationItems } from "@fidelix/fx-miranda";
import { t } from "@lingui/core/macro";

import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { CompanySelect } from "../company/company-select";
import { useNavigationPath, usePathNavigator } from "../utils/url";
import { NavMenu } from "./nav-menu";
import { Permissions } from "../consts";
import { hasPermission } from "../helper";
import { useCurrentUserQuery } from "../user/user.queries";
import { apiRequest } from "../auth/api-request";
export const mainRoutes = ["devices", "groups", "points"] as const;

export type MainRoute = (typeof mainRoutes)[number];

export function getActiveRoute(
  routes: readonly string[] = mainRoutes,
): MainRoute {
  return routes.reduce<{
    routeName: string;
    routeIndex: number;
  }>(
    (a, c) => {
      const idx = location.pathname.lastIndexOf(c);
      if (idx > a.routeIndex) {
        return { routeName: c, routeIndex: idx };
      }
      return a;
    },
    { routeName: "", routeIndex: 0 },
  ).routeName as MainRoute;
}

export function Navbar() {
  const { to } = useNavigationPath();
  const { data: user } = useCurrentUserQuery();
  const { companyId } = useParams();
  const { withSubUrl } = usePathNavigator();
  const activeMainRoute = getActiveRoute(mainRoutes);
  const queryClient = useQueryClient();

  useEffect(() => {
    (async () => {
      await apiRequest.refreshAccessToken();
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const items: TopNavigationItems = [
    {
      to: to("devices"),
      label: t`Devices`,
      isActive: activeMainRoute === "devices",
    },
    {
      to: to("groups"),
      label: t`Groups`,
      isActive: activeMainRoute === "groups",
    },
    {
      to: to("points"),
      label: t`Points`,
      isActive: activeMainRoute === "points",
    },
  ];

  const permissions = user?.permissions || [];

  const menuItems: {
    to: string;
    label: string;
    desc: string;
    permission?: Permissions;
  }[] = [
    {
      to: withSubUrl(`https://analytics.fidelix.com/company/${companyId}`),
      label: t`Fidelix Analytics`,
      desc: t`Monitor and control building operations`,
      permission: Permissions.ANALYTICS,
    },
    {
      to: withSubUrl(`https://assets.fidelix.com/${companyId}/users`),
      label: t`Fidelix Assets`,
      desc: t`Connect devices to the cloud`,
      permission: Permissions.ASSETS,
      active: true,
    },
    {
      to: withSubUrl(`https://admin.fidelix.com/${companyId}/users`),
      label: t`Fidelix Admin`,
      desc: t`Manage customers and user rights`,
      permission: Permissions.COMPANY_ADMIN,
    },
  ].filter((item) => hasPermission(item.permission, permissions));

  return (
    <TopNavigation variant="primary">
      <TopNavigation.Logo text="Assets" menuItems={menuItems} />
      <TopNavigation.Items items={items} />
      <TopNavigation.Actions>
        <CompanySelect />
        <NavMenu id="Member" />
      </TopNavigation.Actions>
    </TopNavigation>
  );
}
